'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import CreditsPurchaseModal from '@/components/CreditsPurchaseModal';

interface InsufficientCreditsModalProps {
  onClose: () => void;
  creditsNeeded: number;
  dailyCredits: number;
  purchasedCredits: number;
}

export default function InsufficientCreditsModal({
  onClose,
  creditsNeeded,
  dailyCredits,
  purchasedCredits,
}: InsufficientCreditsModalProps) {
  const [countdown, setCountdown] = useState({ h: 0, m: 0, s: 0 });
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  useEffect(() => {
    const calcCountdown = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
      tomorrow.setUTCHours(0, 0, 0, 0);
      const diff = Math.max(0, Math.floor((tomorrow.getTime() - now.getTime()) / 1000));
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setCountdown({ h, m, s });
    };
    calcCountdown();
    const interval = setInterval(calcCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const pad = (n: number) => String(n).padStart(2, '0');
  const total = dailyCredits + purchasedCredits;

  if (showPurchaseModal) {
    return (
      <CreditsPurchaseModal
        onClose={() => setShowPurchaseModal(false)}
        onSuccess={onClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-card border border-danger/30 rounded-2xl p-8 shadow-2xl animate-scale-in">
        {/* Icon */}
        <div className="flex justify-center mb-5">
          <div className="w-16 h-16 rounded-2xl bg-danger/15 flex items-center justify-center">
            <Icon name="BoltSlashIcon" size={28} className="text-danger" />
          </div>
        </div>

        <h2 className="text-xl font-bold text-foreground text-center mb-2">
          Ficaste sem créditos
        </h2>
        <p className="text-sm text-muted-foreground text-center mb-6">
          Esta geração requer <span className="font-semibold text-foreground">{creditsNeeded} créditos</span>.
          Tens apenas <span className="font-semibold text-foreground">{total} créditos</span> disponíveis.
        </p>

        {/* Credit breakdown */}
        <div className="bg-muted/30 rounded-xl p-4 mb-4 space-y-2">
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Créditos diários restantes</span>
            <span className="font-semibold text-foreground tabular-nums">{dailyCredits} / 75</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Créditos comprados</span>
            <span className="font-semibold text-foreground tabular-nums">{purchasedCredits}</span>
          </div>
          <div className="h-px bg-border" />
          <div className="flex justify-between text-xs">
            <span className="font-medium text-foreground">Necessário</span>
            <span className="font-bold text-danger tabular-nums">{creditsNeeded}</span>
          </div>
        </div>

        {/* Renewal countdown */}
        <div className="bg-warning/10 border border-warning/20 rounded-xl p-3 mb-6 text-center">
          <p className="text-xs text-muted-foreground mb-1">Os teus créditos gratuitos voltam em</p>
          <p className="text-2xl font-black font-mono text-warning tabular-nums countdown-tick">
            {pad(countdown.h)}:{pad(countdown.m)}:{pad(countdown.s)}
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={() => setShowPurchaseModal(true)}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white gradient-primary hover:opacity-90 active:scale-95 transition-all duration-150"
          >
            <Icon name="CurrencyEuroIcon" size={16} />
            Comprar créditos
          </button>
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-150"
          >
            Voltar ao dashboard
          </button>
        </div>
      </div>
    </div>
  );
}